import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBuilding,
    FaCode,
    FaDiscord,
    FaLinkedin,
    FaMapMarkerAlt,
    FaPalette,
    FaPaperPlane,
    FaShieldAlt,
    FaTwitter,
    FaUniversity
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiCloudStorageOutline,
    TiFlash,
    TiGroupOutline,
    TiMail,
    TiMessages,
    TiPhoneOutline,
    TiRadarOutline,
    TiWorldOutline
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const ContactUs = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [formState, setFormState] = useState("idle"); // idle, sending, success

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormState("sending");
    setTimeout(() => {
      setFormState("success");
    }, 2500);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const channels = [
    {
      title: "Creative & Art",
      brands: "Zero-Art, ZeroArtNFT, Design-Zero",
      desc: "Institutional curation, fine art digital assets, and executive design synergy.",
      icon: <FaPalette />,
      email: "creative@zeroblockchain.xyz"
    },
    {
      title: "Technical Engineering",
      brands: "Modish0161, Zero-Blockchain, ReguSync",
      desc: "L1 protocols, software infrastructure, and regulatory technical synchronization.",
      icon: <FaCode />,
      email: "engineering@zeroblockchain.xyz"
    },
    {
      title: "Financial Intelligence",
      brands: "Moo-Lah, Zero-Bank",
      desc: "Digital banking, wealth growth heuristics, and asset intelligence.",
      icon: <FaUniversity />,
      email: "finance@zeroblockchain.xyz"
    },
    {
      title: "Architectural Tech",
      brands: "Machu Design",
      desc: "Proprietary architectural technology and hardware-software symbiosis.",
      icon: <FaBuilding />,
      email: "architecture@machudesign.com"
    }
  ];

  const deploymentCenters = [
    {
      city: "Manchester",
      role: "Strategic Headquarters",
      address: "Spinningfields, Manchester, UK",
      email: "hq@zeroblockchain.xyz",
      phone: "+44 (0) 161 ZERO"
    },
    {
      city: "London",
      role: "Global Liaison Office",
      address: "Canary Wharf, London, UK",
      email: "london@zeroblockchain.xyz",
      phone: "+44 (0) 20 ZERO"
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/contact-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="contactHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={40}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Direct Nexus"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Communication for the 12-Brand Alliance
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Institutional Liaison Channels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-48">
          {channels.map((channel, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group p-10 rounded-[3rem] bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all hover:bg-white/10 flex flex-col items-center text-center"
              ref={(el) => (sectionRefs.current[index] = el)}
            >
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-2xl text-blue-400 mb-8 group-hover:scale-110 transition-transform shadow-inner">
                {channel.icon}
              </div>
              <h3 className="text-lg font-bold mb-4 text-white uppercase tracking-widest">{channel.title}</h3>
              <div className="text-[9px] font-bold text-blue-500/60 uppercase tracking-[0.2em] mb-6 px-4 py-1 border border-blue-500/20 rounded-full">
                 {channel.brands}
              </div>
              <p className="text-gray-300 text-xs leading-relaxed mb-8 flex-grow">{channel.desc}</p>
              <GlassButton
                onClick={() => window.location.href = `mailto:${channel.email}`}
                variant="blue"
                size="sm"
                className="text-[10px] uppercase tracking-widest"
              >
                 <div className="flex items-center gap-2">
                    <TiMail size={18} /> Initialize Liaison
                 </div>
              </GlassButton>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-16 mb-48">
          {/* High-Fidelity Communication Protocol Form */}
          <div className="w-full lg:w-3/5 p-1 rounded-[4rem] bg-gradient-to-br from-blue-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl">
             <div className="bg-black/80 backdrop-blur-3xl p-12 md:p-16 relative">
                <div className="flex items-center gap-3 text-blue-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-12">
                   <TiMessages className="text-2xl" />
                   Communication Protocol
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-16">
                   Alliance <span className="text-blue-500 font-zentry italic uppercase text-6xl block mt-2">Synergy</span>
                </h2>

                <form onSubmit={handleSubmit} className="space-y-10">
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="group">
                         <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4 group-focus-within:text-blue-400 transition-colors">Identity Path</label>
                         <input 
                           type="text" 
                           placeholder="Full Legal Name" 
                           className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white focus:border-blue-500/50 outline-none transition-all placeholder:text-gray-700 font-medium"
                           required
                         />
                      </div>
                      <div className="group">
                         <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4 group-focus-within:text-blue-400 transition-colors">Communication Link</label>
                         <input 
                           type="email" 
                           placeholder="institutional@domain.xyz" 
                           className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white focus:border-blue-500/50 outline-none transition-all placeholder:text-gray-700 font-medium"
                           required
                         />
                      </div>
                   </div>

                   <div className="group font-bold">
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4 group-focus-within:text-blue-400 transition-colors">Liaison Department</label>
                      <select className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white focus:border-blue-500/50 outline-none transition-all appearance-none cursor-pointer">
                         <option className="bg-black">Technical Integration (Modish/Blockchain)</option>
                         <option className="bg-black">Creative Partnership (Zero-Art/Design)</option>
                         <option className="bg-black">Institutional Finance (Zero-Bank/Moo-Lah)</option>
                         <option className="bg-black">Architectural Synergy (Machu Design)</option>
                         <option className="bg-black">Global Regulatory (ReguSync)</option>
                      </select>
                   </div>

                   <div className="group">
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4 group-focus-within:text-blue-400 transition-colors">Synergy Directive</label>
                      <textarea 
                        rows="5"
                        placeholder="Define the scope of alliance..." 
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white focus:border-blue-500/50 outline-none transition-all placeholder:text-gray-700 font-medium resize-none text-sm"
                        required
                      ></textarea>
                   </div>

                   <GlassButton
                     type="submit"
                     disabled={formState !== "idle"}
                     className="w-full py-8 text-xl uppercase tracking-[0.2em] shadow-xl"
                     variant={formState === "success" ? "emerald" : "blue"}
                     size="lg"
                   >
                     <div className="flex items-center justify-center gap-4">
                       {formState === "idle" && <><FaPaperPlane /> Initialize Transmission</>}
                       {formState === "sending" && <><div className="w-6 h-6 rounded-full border-2 border-black/30 border-t-black animate-spin" /> Synchronizing...</>}
                       {formState === "success" && <><TiFlash /> Transmission Synchronized</>}
                     </div>
                   </GlassButton>
                </form>
             </div>
          </div>

          {/* Strategic Deployment Centers */}
          <div className="w-full lg:w-2/5 flex flex-col gap-8">
             {deploymentCenters.map((center, index) => (
               <motion.div
                 key={index}
                 initial={{ opacity: 0, x: 20 }}
                 whileInView={{ opacity: 1, x: 0 }}
                 viewport={{ once: true }}
                 className="p-12 rounded-[4rem] bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all flex flex-col relative overflow-hidden group h-full"
               >
                  <div className="absolute -top-10 -right-10 text-[180px] text-white/[0.02] group-hover:text-blue-500/[0.04] transition-colors pointer-events-none">
                     <TiRadarOutline />
                  </div>
                  
                  <div className="flex items-center gap-3 text-blue-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-8">
                     <TiWorldOutline className="text-xl" />
                     Strategic Asset
                  </div>
                  <h3 className="text-4xl font-bold text-white mb-2">{center.city}</h3>
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-10">{center.role}</div>
                  
                  <div className="space-y-6 flex-grow">
                     <div className="flex items-start gap-4 text-gray-300">
                        <FaMapMarkerAlt className="text-blue-500 mt-1" />
                        <span className="text-sm italic">{center.address}</span>
                     </div>
                     <div className="flex items-center gap-4 text-gray-300">
                        <TiMail className="text-blue-500" />
                        <span className="text-sm">{center.email}</span>
                     </div>
                     <div className="flex items-center gap-4 text-gray-300">
                        <TiPhoneOutline className="text-blue-500" size={18} />
                        <span className="text-sm">{center.phone}</span>
                     </div>
                  </div>
                  
                  <div className="pt-10 flex gap-6 text-xl text-gray-600 group-hover:text-blue-400/60 transition-colors">
                     <FaLinkedin className="hover:text-blue-400 cursor-pointer transition-colors" />
                     <FaTwitter className="hover:text-blue-400 cursor-pointer transition-colors" />
                     <FaDiscord className="hover:text-blue-400 cursor-pointer transition-colors" />
                  </div>
               </motion.div>
             ))}
          </div>
        </div>

        {/* Final Alliance CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-blue-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[2.5rem] bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-5xl text-blue-400 mx-auto mb-16 shadow-[0_0_50px_rgba(59,130,246,0.2)]">
                <TiGroupOutline />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Initialize <span className="font-zentry italic text-blue-400 text-8xl uppercase tracking-tighter block mt-4">Synergy</span>
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                Connect directly with the 12-brand alliance and catalyze institutional-grade impact across the Zero-Ecosystem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   className="px-16 py-8 text-xl"
                   variant="blue"
                   size="lg"
                   onClick={() => {}} // Placeholder
                 >
                   Schedule Briefing
                 </GlassButton>
                 <GlassButton
                   className="px-16 py-8 text-xl"
                   variant="gray"
                   size="lg"
                   onClick={() => window.open('/about', '_self')}
                 >
                    <div className="flex items-center gap-4">
                      Vision Protocol <TiArrowRight className="text-2xl text-blue-400" />
                    </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaShieldAlt /> SECURE NEXUS</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><TiCloudStorageOutline size={18} /> QUANTUM STORAGE</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><TiRadarOutline size={18} /> ACTIVE MONITORING</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-blue-500/30 rounded-full shadow-lg shadow-blue-500/10 cursor-pointer hover:bg-black/60 hover:border-blue-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-blue-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ContactUs;
